import { useState } from "react"

import type {
  BatteryEstimateBasis,
  BatteryModeledBillingScope,
  BatteryPeakUsageScenarioDto,
} from "~/api/dto/battery-arbitrage.dto"

import {
  arbitrageBasisQualifier,
  arbitrageBillLabel,
} from "./arbitrage-assumptions"
import { Eyebrow, GradText, Panel, TINT_TEXT, type Tint } from "./primitives"
import { SavingsDial } from "./SavingsDial"
import { formatUsd, formatUsdExact, reducesUtilityBill } from "./view-helpers"

const kwh = new Intl.NumberFormat(`en-US`, { maximumFractionDigits: 0 })

function peakUsageCopy(input: {
  loadShapeSource: `measured_interval` | `typical_scaled`
  measurementBasis: string
  baseline: boolean
  estimateBasis: BatteryEstimateBasis
  modeledBillingScope: BatteryModeledBillingScope
}): {
  eyebrow: string
  reset: string
  totals: string
  discloseEstimate: boolean
} {
  const scope = arbitrageBillLabel(input)
  const greenButtonGrid =
    input.loadShapeSource === `measured_interval` &&
    input.measurementBasis === `GRID_IMPORT`
  if (greenButtonGrid) {
    return {
      eyebrow: input.baseline
        ? `Recorded peak grid usage · Green Button · ${scope}`
        : `What-if estimate · Hypothetical peak grid usage`,
      reset: `Reset to recorded usage`,
      totals: `Monthly grid-import totals stay fixed. Exploring does not change your proposal.`,
      discloseEstimate: true,
    }
  }
  switch (input.loadShapeSource) {
    case `measured_interval`:
    case `typical_scaled`:
      return {
        eyebrow: `${input.baseline ? `Saved usage` : `What-if estimate`} · ${scope}`,
        reset: `Reset to saved usage`,
        totals: `Monthly usage totals stay fixed. Exploring does not change your proposal.`,
        discloseEstimate: false,
      }
    default: {
      const _never: never = input.loadShapeSource
      void _never
      return {
        eyebrow: `${input.baseline ? `Saved usage` : `What-if estimate`} · ${scope}`,
        reset: `Reset to saved usage`,
        totals: `Monthly usage totals stay fixed. Exploring does not change your proposal.`,
        discloseEstimate: false,
      }
    }
  }
}

export function PeakUsageScenario({
  scenario,
  interactive = true,
  loadShapeSource,
  measurementBasis,
  estimateBasis,
  modeledBillingScope,
}: {
  scenario: BatteryPeakUsageScenarioDto
  interactive?: boolean
  loadShapeSource: `measured_interval` | `typical_scaled`
  measurementBasis: string
  estimateBasis: BatteryEstimateBasis
  modeledBillingScope: BatteryModeledBillingScope
}): JSX.Element | null {
  const [index, setIndex] = useState(scenario.baselinePointIndex)
  const selectedIndex = interactive ? index : scenario.baselinePointIndex
  const point = scenario.points[selectedIndex]
  if (!point) return null

  const baseline = selectedIndex === scenario.baselinePointIndex
  const estimate = { estimateBasis, modeledBillingScope }
  const copy = peakUsageCopy({
    loadShapeSource,
    measurementBasis,
    baseline,
    estimateBasis,
    modeledBillingScope,
  })
  const saves = reducesUtilityBill(point.annualSavingsUsd)
  const figures: Array<[string, string, Tint]> = [
    [
      `Average daily savings`,
      formatUsdExact(point.averageDailySavingsUsd),
      `text`,
    ],
    [
      `Average monthly savings`,
      formatUsdExact(point.averageMonthlySavingsUsd),
      saves ? `accent` : `text`,
    ],
    [
      `Year 1 savings`,
      formatUsd(point.annualSavingsUsd),
      saves ? `success` : `text`,
    ],
  ]

  return (
    <section
      aria-label={
        interactive ? `Peak-usage savings scenario` : `Saved peak-usage savings`
      }
    >
      <Panel className="grid items-center gap-6 p-5 sm:p-8 lg:grid-cols-[300px_1fr] lg:gap-12">
        <div className="mx-auto w-full max-w-[300px]">
          <SavingsDial
            value={selectedIndex}
            interactive={interactive}
            min={0}
            max={scenario.points.length - 1}
            step={1}
            size={300}
            onChange={setIndex}
            label="Monthly peak usage"
            valueText={`${kwh.format(point.peakKwhPerMonth)} kWh per month`}
          >
            <div>
              <Eyebrow className="mb-2">Peak usage</Eyebrow>
              <div className="font-display text-5xl leading-none font-semibold tracking-tight tabular-nums">
                <GradText>{kwh.format(point.peakKwhPerMonth)}</GradText>
              </div>
              <div className="mt-2 text-xs text-bp-text-muted">kWh / month</div>
            </div>
          </SavingsDial>
          {interactive ? (
            <p className="mt-2 text-center text-xs text-bp-text-muted">
              Drag or use arrow keys to explore peak usage.
            </p>
          ) : null}
        </div>
        <div className="min-w-0">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <Eyebrow>{copy.eyebrow}</Eyebrow>
            {interactive ? (
              <button
                type="button"
                disabled={baseline}
                onClick={() => setIndex(scenario.baselinePointIndex)}
                className="rounded text-xs font-medium text-bp-accent underline underline-offset-4 focus-visible:ring-2 focus-visible:ring-bp-accent focus-visible:outline-none disabled:cursor-default disabled:text-bp-text-muted disabled:no-underline"
              >
                {copy.reset}
              </button>
            ) : null}
          </div>
          {copy.discloseEstimate ? (
            <p className="mb-4 text-xs text-bp-text-muted">
              {arbitrageBasisQualifier(estimate)}. {arbitrageBillLabel(estimate)}
              . Savings remain an estimate.
            </p>
          ) : null}
          <div className="mb-4 grid gap-3 sm:grid-cols-2">
            <div className="flex items-center justify-between gap-3 rounded-lg border border-bp-border bg-bp-elevated px-4 py-4">
              <span className="text-xs text-bp-text-muted">Energy shifted</span>
              <span className="font-semibold text-bp-accent tabular-nums">
                {kwh.format(point.shiftedKwhPerMonth)} kWh/mo
              </span>
            </div>
            <div className="flex items-center justify-between gap-3 rounded-lg border border-bp-border bg-bp-elevated px-4 py-4">
              <span className="text-xs text-bp-text-muted">
                Savings per shifted kWh
              </span>
              <span className="font-semibold tabular-nums">
                {point.savingsPerShiftedKwhUsd === null
                  ? `—`
                  : `${formatUsdExact(point.savingsPerShiftedKwhUsd)}/kWh`}
              </span>
            </div>
          </div>
          <div
            className="grid gap-3 sm:grid-cols-3"
            aria-live="polite"
            aria-atomic="true"
          >
            {figures.map(([label, value, tint]) => (
              <div
                key={label}
                className="rounded-lg border border-bp-border bg-bp-elevated px-5 py-5"
              >
                <div
                  className={`font-display text-[length:var(--step-2)] leading-none font-semibold tracking-tight tabular-nums ${TINT_TEXT[tint]}`}
                >
                  {value}
                </div>
                <div className="mt-2 text-xs text-bp-text-muted">{label}</div>
              </div>
            ))}
          </div>
          {interactive ? (
            <p className="mt-4 text-xs text-bp-text-muted">
              {copy.totals}
              {!baseline &&
              scenario.estimateMethod === `provider_bill_calibrated`
                ? ` What-if savings are estimates calibrated to the saved monthly bills. Utility tiers and credits are not recalculated.`
                : null}
            </p>
          ) : null}
        </div>
      </Panel>
    </section>
  )
}
